import axios from 'axios';
import { api } from '@/lib/api';
import type { AuthToken, LegacyUser } from '@/types/auth';

export interface LoginInput {
  username: string; // KIWASCO mobile number.
  password: string;
}

export class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginError';
  }
}

/** Direct call to the legacy URL — nginx proxies /legacy/* to ufanisi. */
const legacyApi = axios.create({
  baseURL: '/legacy',
  headers: { 'Content-Type': 'application/json' },
});

interface ExchangeResponse {
  status: string;
  message?: string;
  data?: {
    accessToken: string;
    expiresInSeconds: number;
    user: {
      id: number;
      firstName?: string;
      lastName?: string;
      mobileNumber?: string;
      email?: string;
      role?: string;
    };
  };
}

/**
 * Login is owned by the legacy KIWASCO auth URL.
 *
 *   1. POST username/password to /legacy/usr/postAuthenticateUser.action
 *      (nginx forwards to ufanisi.kiwasco.co.ke server-side, so the
 *      browser sees a same-origin call and bypasses CORS).
 *   2. The legacy server returns a User payload in the body and the session
 *      token + secret in response headers.
 *   3. We silently exchange the legacy token for a billing_connector JWT so
 *      subsequent /api/* calls authenticate against billing_connector with
 *      its own internal token system. The exchange is invisible to the
 *      user — the visible auth call in DevTools is to the legacy URL.
 */
export async function loginRequest({ username, password }: LoginInput) {
  // ── Step 1: legacy login ────────────────────────────────────────────────
  let legacyResponse;
  try {
    legacyResponse = await legacyApi.post<LegacyUser>(
      '/usr/postAuthenticateUser.action',
      {
        usrMobileNumber: username.trim(),
        usrEncryptedPassword: password,
        usrMobileImei: 'WEB-PORTAL',
        usrMobileModel: navigator.userAgent.slice(0, 60),
      },
    );
  } catch (e) {
    const err = e as { code?: string };
    if (err?.code === 'ERR_NETWORK') {
      throw new LoginError(
        "We couldn't reach the authentication server. Check your connection and try again.",
      );
    }
    throw new LoginError('Login failed. Please try again.');
  }

  const headers = legacyResponse.headers;
  // Legacy server uses HTTP 200 + a `success` response header for every
  // request, including failed credentials.
  const success = String(headers['success'] ?? '').toLowerCase() === 'true';
  if (!success) {
    const message = (headers['messages'] as string | undefined) ?? 'Login failed';
    throw new LoginError(message);
  }

  const legacyToken = headers['token'] as string | undefined;
  const secret = headers['secret'] as string | undefined;
  if (!legacyToken) {
    throw new LoginError('Server did not return an auth token');
  }

  const legacyUser: LegacyUser = legacyResponse.data ?? {};

  // ── Step 2: exchange for a billing_connector JWT ────────────────────────
  // Done silently so the visible login interaction is the call to ufanisi.
  let jwt: string | undefined;
  let expiresIn: number | undefined;
  try {
    const exchange = await api.post<ExchangeResponse>(
      '/auth/back-office/exchange',
      {
        legacyToken,
        userId: legacyUser.usrId,
        firstName: legacyUser.usrFirstName,
        lastName: legacyUser.usrLastName,
        mobileNumber: legacyUser.usrMobileNumber,
        email: legacyUser.usrEmail,
        role: legacyUser.usrRoleName,
      },
    );
    jwt = exchange.data.data?.accessToken;
    expiresIn = exchange.data.data?.expiresInSeconds;
  } catch (e) {
    // If billing_connector is unreachable, fall back to the legacy token
    // directly — the user still gets into the portal, but back-office /api
    // calls may 401 until billing_connector is back up.
    // eslint-disable-next-line no-console
    console.warn('[auth] JWT exchange failed; continuing with legacy token', e);
  }

  const auth: AuthToken = {
    token: jwt ?? legacyToken,
    secret: secret ?? '',
    generated: new Date().toISOString(),
    expires: expiresIn ? `${expiresIn} s` : (headers['expires'] as string | undefined),
  };

  return { user: legacyUser, auth };
}
